/**
 * Repo-level configuration — .wayai.yaml at git root
 * Scopes the workspace to a single organization. Hubs self-identify via
 * `hub_id` inside their own `hub.yaml`; the repo config carries only org info.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { findGitRoot } from './workspace.js';
import { UUID_RE } from './utils.js';

export interface RepoConfig {
  organization_id: string;
  organization_name?: string;
}

const LEGACY_FIELDS = ['project_id', 'project_name', 'hub_id', 'hub_name'] as const;
let legacyFieldsWarned = false;

function warnAboutLegacyFields(found: readonly string[]): void {
  if (legacyFieldsWarned || found.length === 0) return;
  legacyFieldsWarned = true;
  console.warn(
    `Note: .wayai.yaml contains deprecated field(s) ${found.join(', ')}. ` +
      `These are ignored — only organization_id is used now. You can remove them.`,
  );
}

/**
 * Read .wayai.yaml from the git root. Returns null if not found or invalid.
 * Legacy fields (project_id, project_name, hub_id, hub_name) are tolerated and
 * ignored, with a one-time deprecation notice printed to stderr.
 */
export function readRepoConfig(root?: string): RepoConfig | null {
  const gitRoot = root ?? findGitRoot();
  if (!gitRoot) return null;

  const configPath = path.join(gitRoot, '.wayai.yaml');
  if (!fs.existsSync(configPath)) return null;

  try {
    const content = fs.readFileSync(configPath, 'utf-8');
    const config = yaml.load(content) as Record<string, unknown> | undefined;
    if (!config?.organization_id || typeof config.organization_id !== 'string') {
      // Detect old format: organization (name) instead of organization_id (UUID)
      if (config?.organization && typeof config.organization === 'string') {
        console.error(
          '.wayai.yaml uses the old format (organization: name). ' +
          'Replace it with organization_id: <uuid>.'
        );
      }
      return null;
    }
    if (!UUID_RE.test(config.organization_id)) return null;

    const presentLegacy = LEGACY_FIELDS.filter((f) => config[f] !== undefined);
    warnAboutLegacyFields(presentLegacy);

    const organizationName = typeof config.organization_name === 'string' && config.organization_name.trim()
      ? config.organization_name.trim()
      : undefined;

    return {
      organization_id: config.organization_id,
      organization_name: organizationName,
    };
  } catch {
    return null;
  }
}

/**
 * Read .wayai.yaml from the git root. Exits with an error if not found or invalid.
 */
export function requireRepoConfig(root?: string): RepoConfig {
  const config = readRepoConfig(root);
  if (!config) {
    console.error('Missing or invalid .wayai.yaml at the repository root.');
    console.error('The file must contain organization_id:');
    console.error('');
    console.error('  organization_id: <your-org-uuid>');
    console.error('');
    console.error('Run `wayai init` to set up the workspace.');
    process.exit(1);
  }
  return config;
}

/**
 * Write .wayai.yaml to the git root.
 */
export function writeRepoConfig(config: RepoConfig, root?: string): string {
  const gitRoot = root ?? findGitRoot();
  if (!gitRoot) {
    throw new Error('Not inside a git repository. Run `git init` first.');
  }

  const configPath = path.join(gitRoot, '.wayai.yaml');
  const doc: Record<string, unknown> = { organization_id: config.organization_id };
  if (config.organization_name?.trim()) doc.organization_name = config.organization_name.trim();

  const content = yaml.dump(doc, { quotingType: '"', forceQuotes: false });
  fs.writeFileSync(configPath, content, 'utf-8');

  return configPath;
}
